import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@tremor/react'
import ErrorModal from '../../../../Components/ErrorModal';
import SuccesModal from '../../../../Components/SuccesModal';
import DeleteAccountModal from '../../../../Components/DeleteAccountModal';
import { deleteUser, getEmailSettings, updateEmailSettings, updatePassword, requestGdprExport } from '../../../../Requests/User/UserRequests';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import PrivacyDashboard from '../../../../Components/Privacy/PrivacyDashboard';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';

function AccountSettings() {
    const { t } = useTranslation();
    const [newPasswordIsLoading, setNewPasswordIsLoading] = useState(false)
    const [updateEmailSettingsLoading, setUpdateEmailSettingsLoading] = useState(false)
    const [gdprExportLoading, setGdprExportLoading] = useState(false)
    const [feedbackModal, setFeedbackModal] = useState(null)
    const [emailSettings, setEmailSettings] = useState(null)
    const { isCollapsed } = useSidebar();

    let handleCancelUser = async (event) => {
        let reset = await setFeedbackModal(null)
        event.preventDefault();
        setFeedbackModal(<DeleteAccountModal
            title={t('AccountSettingsPage.DeleteModalTitle')}
            body={t('AccountSettingsPage.DeleteModalText')}
            continue={t('AccountSettingsPage.DeleteModalButton')}
            navLink="/login"
            fun={deleteUser}
        ></DeleteAccountModal>)
    }

    let handleChangePassword = (event) => {
        event.preventDefault();
        setFeedbackModal(null)
        setNewPasswordIsLoading(true)
        let response = updatePassword(
            event.target.current_password.value,
            event.target.password.value,
            event.target.password_confirmation.value
        );
        response.
            then((res) => {
                setNewPasswordIsLoading(false);
                setFeedbackModal(
                    <SuccesModal
                        title={t('AccountSettingsPage.PasswordSuccessModalTitle')}
                        body={t('AccountSettingsPage.PasswordSuccessModalText')}
                        continue={t('AccountSettingsPage.PasswordSuccessModalButton')}
                        handleClose={() => { setFeedbackModal(null) }}
                    />
                );
            })
            .catch((error) => {
                setNewPasswordIsLoading(false);
                setFeedbackModal(<ErrorModal
                    title={t('AccountSettingsPage.PasswordFailModalTitle')}
                    body={t('AccountSettingsPage.PasswordFailModalText')}
                    continue={t('AccountSettingsPage.PasswordFailModalButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
                )
            })
    }

    let fetchEmailSettings = () => {
        let response = getEmailSettings()
        response.then((res) => {
            setEmailSettings(res.data)
        }).catch((e) => {

        })
    }

    let handleGdprExport = async (format = 'csv') => {
        setFeedbackModal(null)
        setGdprExportLoading(true)

        try {
            const response = await requestGdprExport(format);

            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const today = new Date().toISOString().split('T')[0];
            link.download = `gdpr_data_export_${today}.zip`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setGdprExportLoading(false);
            setFeedbackModal(
                <SuccesModal
                    title={t('AccountSettingsPage.GdprExportSuccessTitle')}
                    body={t('AccountSettingsPage.GdprExportSuccessText')}
                    continue={t('AccountSettingsPage.GdprExportSuccessButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
            );
        } catch (error) {
            setGdprExportLoading(false);
            setFeedbackModal(
                <ErrorModal
                    title={t('AccountSettingsPage.GdprExportErrorTitle')}
                    body={t('AccountSettingsPage.GdprExportErrorText')}
                    continue={t('AccountSettingsPage.GdprExportErrorButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
            );
        }
    }

    useEffect(() => {
        fetchEmailSettings()
    }, [])

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 md:pt-12 w-full flex justify-center`}>
            <div className='flex flex-col w-full justify-center items-center px-4 gap-4'>
                <div className='bg-white max-w-7xl rounded-2xl w-full'>
                    <SectionHeader title={t('AccountSettingsPage.PageTitle')} subtitle={t('AccountSettingsPage.PageDescription')} />
                </div>

                <Card className='w-full max-w-7xl'>
                    <form onSubmit={handleChangePassword}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('AccountSettingsPage.PasswordSettingTitle')}</h3>
                        <div className="flex flex-col gap-4 max-w-md">
                            <div>
                                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('AccountSettingsPage.PasswordSettingCurrentPassword')}
                                </label>
                                <input
                                    type="password"
                                    name="current_password"
                                    id="current_password"
                                    autoComplete="current-password"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100 text-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('AccountSettingsPage.PasswordSettingNewPassword')}
                                </label>
                                <input
                                    type="password"
                                    name="password"
                                    id="password"
                                    autoComplete="new-password"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100 text-sm"
                                />
                            </div>
                            <div>
                                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('AccountSettingsPage.PasswordSettingRepeatPassword')}
                                </label>
                                <input
                                    type="password"
                                    name="password_confirmation"
                                    id="password_confirmation"
                                    autoComplete="new-password"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100 text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                type="submit"
                                disabled={newPasswordIsLoading}
                                className="inline-flex justify-center rounded-md border border-transparent bg-iavisus-100 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-iavisus-200 focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('AccountSettingsPage.PasswordSettingAction')}
                            </button>
                        </div>
                    </form>
                </Card>

                <Card className='w-full max-w-7xl'>
                    <PrivacyDashboard />
                </Card>

                <Card className='w-full max-w-7xl'>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('AccountSettingsPage.GdprExportTitle')}</h3>
                    <p className="text-sm text-gray-500 mb-4">{t('AccountSettingsPage.GdprExportDescription')}</p>
                    <button
                        onClick={() => handleGdprExport('csv')}
                        disabled={gdprExportLoading}
                        type="button"
                        className="inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {gdprExportLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('AccountSettingsPage.GdprExportLoadingText')}
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t('AccountSettingsPage.GdprExportButton')}
                            </>
                        )}
                    </button>
                    <p className="mt-2 text-xs text-gray-500">{t('AccountSettingsPage.GdprExportNote')}</p>
                </Card>

                <Card className='w-full max-w-7xl'>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('AccountSettingsPage.DeleteAccountTitle')}</h3>
                    <p className="text-sm text-gray-500 mb-4">{t('AccountSettingsPage.DeleteAccountText')}</p>
                    <a
                        href="/login"
                        onClick={handleCancelUser}
                        className="inline-flex rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        {t('AccountSettingsPage.DeleteAccountAction')}
                    </a>
                </Card>
            </div>
            {feedbackModal}
        </div>
    )
}

export default AccountSettings
